import React, { useCallback, useMemo } from "react";

import { ColDef, IHeaderParams } from "ag-grid-community";
import styled from "styled-components/macro";

import AgGrid from "components/AgGrid/AgGrid";
import {
  FieldConfiguration,
  getSideLoadedFieldConfigurationsForUser,
} from "components/Business/SideLoadedBusinesses/CompareModal/fieldConfigurations";
import { ModalTableWrapper } from "components/AgGrid/TableWrapper";
import { Column } from "components/Layout";
import { ReactSelect } from "components/SearchableSelector";

import { useTheme } from "hooks";

import {
  collectsManyColumns,
  slotIndicesFor,
} from "lib/sideLoadedFieldMapping";

interface RowCountStatusPanelParams {
  shownCount: number;
  totalCount: number;
}

function RowCountStatusPanel({
  shownCount,
  totalCount,
}: RowCountStatusPanelParams) {
  return (
    <div className="ag-status-name-value">
      Showing {shownCount} of {totalCount} rows
    </div>
  );
}

const HeaderName = styled.div`
  font-weight: bold;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
`;

const HeaderSelect = styled(ReactSelect)<{ $isMapped: boolean }>`
  font-size: 11px;

  .react-select__control {
    min-height: 26px;
    ${({ $isMapped, theme }) =>
      $isMapped ? "" : `border: 1px dashed ${theme.colors.gray44};`}
  }
  .react-select__value-container,
  .react-select__indicators {
    min-height: 26px;
    padding-top: 0;
    padding-bottom: 0;
  }
  .react-select__indicator {
    padding: 4px;
  }
`;

interface TargetFieldOption {
  value: string;
  label: string;
  takenBy?: string;
  // An additive target with no slot left for this column.
  isFull?: boolean;
  slotCount?: number;
}

function formatOptionLabel(option: TargetFieldOption): React.ReactNode {
  if (option.isFull) {
    return `${option.label} - all ${option.slotCount} slots used`;
  }
  return option.takenBy
    ? `${option.label} - mapped to ${option.takenBy}`
    : option.label;
}

// Through react-select's hook, not CSS - its option classes outrank stylesheets.
function getOptionStyles(theme) {
  return {
    option: (base, state: { data: TargetFieldOption; isFocused: boolean }) => ({
      ...base,
      color: state.isFocused
        ? base.color
        : state.data.takenBy || state.data.isFull
          ? theme.colors.gray44
          : theme.colors.titleDark,
    }),
  };
}

interface MappingHeaderParams extends IHeaderParams {
  // The file's header, not displayName - ag-grid humanises that and only the raw
  // one keys a row.
  sourceColumn: string;
  configurations: FieldConfiguration[];
  targetFieldKey: string | null;
  columnByTarget: Record<string, string>;
  additiveTargets: Set<string>;
  fullAdditiveTargets: Set<string>;
  onChangeTarget: (sourceColumn: string, targetFieldKey: string | null) => void;
}

function MappingHeader(params: MappingHeaderParams) {
  const {
    sourceColumn,
    configurations,
    targetFieldKey,
    columnByTarget,
    additiveTargets,
    fullAdditiveTargets,
    onChangeTarget,
  } = params;
  const theme = useTheme();
  const optionStyles = useMemo(() => getOptionStyles(theme), [theme]);

  const options = useMemo<TargetFieldOption[]>(
    () =>
      configurations.map(configuration => {
        const takenBy = columnByTarget[configuration.value];
        const takenElsewhere =
          Boolean(takenBy) &&
          takenBy !== sourceColumn &&
          !additiveTargets.has(configuration.value);
        return {
          value: configuration.value,
          label: configuration.label,
          takenBy: takenElsewhere ? takenBy : undefined,
          // Still offered to columns holding a slot, so one can be re-pointed directly.
          isFull:
            fullAdditiveTargets.has(configuration.value) &&
            targetFieldKey !== configuration.value,
          slotCount: slotIndicesFor(configuration).length,
        };
      }),
    [
      configurations,
      columnByTarget,
      sourceColumn,
      additiveTargets,
      fullAdditiveTargets,
      targetFieldKey,
    ],
  );

  const selectedOption = options.find(
    option => option.value === targetFieldKey,
  );

  return (
    <Column
      full
      // Drag-to-reorder starts on mousedown, so stopping the click is too late.
      onMouseDown={event => event.stopPropagation()}
      onTouchStart={event => event.stopPropagation()}
      onClick={event => event.stopPropagation()}
    >
      <HeaderName title={sourceColumn}>{sourceColumn}</HeaderName>
      <HeaderSelect
        $isMapped={Boolean(targetFieldKey)}
        value={selectedOption || null}
        options={options}
        formatOptionLabel={formatOptionLabel}
        isOptionDisabled={(option: TargetFieldOption) => Boolean(option.isFull)}
        styles={optionStyles}
        onChange={option => onChangeTarget(sourceColumn, option?.value || null)}
        placeholder="Not mapped..."
        isClearable
        menuPortalTarget={document.body}
        menuPlacement="auto"
      />
    </Column>
  );
}

interface ColumnMappingGridProps {
  sourceColumns: string[];
  rows: Record<string, unknown>[];
  // The real row count; rows is only ever a preview.
  totalRowCount: number;
  entries: SideLoadedDataMappingEntry[];
  onChange: (entries: SideLoadedDataMappingEntry[]) => void;
  userRoleContentTypeName: string | null;
}

// From sourceColumns, not generated from rows - generation samples the first row,
// which is wrong the moment one is missing a key.
export function ColumnMappingGrid({
  sourceColumns,
  rows,
  totalRowCount,
  entries,
  onChange,
  userRoleContentTypeName,
}: ColumnMappingGridProps): React.JSX.Element {
  const configurations = useMemo(
    () => getSideLoadedFieldConfigurationsForUser(userRoleContentTypeName),
    [userRoleContentTypeName],
  );

  const additiveTargets = useMemo(
    () =>
      new Set(
        configurations
          .filter(collectsManyColumns)
          .map(configuration => configuration.value),
      ),
    [configurations],
  );

  // Only as many columns as the compare modal has slots; extras were dropped.
  const fullAdditiveTargets = useMemo(() => {
    const countByTarget = entries.reduce<Record<string, number>>(
      (counts, entry) => {
        if (additiveTargets.has(entry.targetFieldKey)) {
          counts[entry.targetFieldKey] =
            (counts[entry.targetFieldKey] || 0) + 1;
        }
        return counts;
      },
      {},
    );

    return new Set(
      configurations
        .filter(
          configuration =>
            (countByTarget[configuration.value] || 0) >=
            slotIndicesFor(configuration).length,
        )
        .map(configuration => configuration.value),
    );
  }, [entries, additiveTargets, configurations]);

  const targetByColumn = useMemo(
    () =>
      entries.reduce<Record<string, string>>((byColumn, entry) => {
        byColumn[entry.sourceColumn] = entry.targetFieldKey;
        return byColumn;
      }, {}),
    [entries],
  );

  const columnByTarget = useMemo(
    () =>
      entries.reduce<Record<string, string>>((byTarget, entry) => {
        // First wins, matching how an additive target reads its columns in order.
        if (!byTarget[entry.targetFieldKey]) {
          byTarget[entry.targetFieldKey] = entry.sourceColumn;
        }
        return byTarget;
      }, {}),
    [entries],
  );

  const onChangeTarget = useCallback(
    (sourceColumn: string, targetFieldKey: string | null) => {
      const withoutColumn = entries.filter(
        entry => entry.sourceColumn !== sourceColumn,
      );
      if (!targetFieldKey) {
        onChange(withoutColumn);
        return;
      }
      // A non-additive target takes one column, so re-pointing it moves the column.
      const freed = additiveTargets.has(targetFieldKey)
        ? withoutColumn
        : withoutColumn.filter(
            entry => entry.targetFieldKey !== targetFieldKey,
          );
      // Rebuilt in file order, which is what decides the notes slots later.
      const next = freed.concat({ sourceColumn, targetFieldKey });
      onChange(
        sourceColumns
          .map(column => next.find(entry => entry.sourceColumn === column))
          .filter(Boolean),
      );
    },
    [entries, onChange, sourceColumns, additiveTargets],
  );

  const columnDefs = useMemo<ColDef[]>(
    () =>
      sourceColumns.map(sourceColumn => ({
        colId: sourceColumn,
        field: sourceColumn,
        headerComponent: MappingHeader,
        headerComponentParams: {
          sourceColumn,
          configurations,
          targetFieldKey: targetByColumn[sourceColumn] || null,
          columnByTarget,
          additiveTargets,
          fullAdditiveTargets,
          onChangeTarget,
        },
        minWidth: 170,
        // File order decides the notes slots.
        suppressMovable: true,
        sortable: false,
      })),
    [
      sourceColumns,
      configurations,
      targetByColumn,
      columnByTarget,
      additiveTargets,
      fullAdditiveTargets,
      onChangeTarget,
    ],
  );

  const statusBar = useMemo(
    () => ({
      statusPanels: [
        {
          statusPanel: RowCountStatusPanel,
          statusPanelParams: {
            shownCount: rows.length,
            totalCount: totalRowCount,
          },
          align: "left",
        },
      ],
    }),
    [rows.length, totalRowCount],
  );

  return (
    <ModalTableWrapper minHeight={320}>
      <AgGrid
        columnDefs={columnDefs}
        rowData={rows}
        headerHeight={68}
        statusBar={statusBar}
      />
    </ModalTableWrapper>
  );
}
